/* https://github.com/maplibre/maplibre-gl-js/blob/main/src/ui/handler/scroll_zoom.ts#L183 */
import Wasm from "./wasm";

type TType = "wheel" | "trackpad" | null;

// deltaY value for mouse scroll wheel identification
const WHEEL_ZOOM_DELTA = 4.000244140625;
const WHEEL_TO = 100;
const WHEEL_STEP = 0.25;

export default class WasmScroll {
	#wasm: Wasm;

	#lastWheelEventTime: number;

	#type: TType;

	#timeout: ReturnType<typeof setTimeout>;

	#active: boolean;

	#zooming: boolean;

	#startZoom: number;

	#zoomAnim: ReturnType<typeof setTimeout>;

	constructor(wasm: Wasm, elem: HTMLElement) {
		this.#wasm = wasm;
		this.#lastWheelEventTime = 0;
		this.#type = null;
		this.#timeout = null;
		this.#zoomAnim = null;
		this.#active = false;
		this.#zooming = false;
		this.#startZoom = 0;

		// kolecko
		elem.addEventListener("wheel", event => {
			let value = event.deltaMode === WheelEvent.DOM_DELTA_LINE
				? event.deltaY * 40
				: event.deltaY;

			const now = Date.now();
			const timeDelta = now - (this.#lastWheelEventTime || 0);

			this.#lastWheelEventTime = now;

			if (value !== 0 && (value % WHEEL_ZOOM_DELTA) === 0) {
				// This one is definitely a mouse wheel event.
				this.#type = "wheel";
			} else if (value !== 0 && Math.abs(value) < 4) {
				// This one is definitely a trackpad event because it is so small.
				this.#type = "trackpad";
			} else if (timeDelta > 400) {
				// This is likely a new scroll action.
				this.#type = null;

				// Start a timeout in case this was a singular event, and delay it by up to 40ms.
				this.#timeout = setTimeout(this.#onTimeout, 40, event);
			} else if (!this.#type) {
				// This is a repeating event, but we don"t know the type of event just yet.
				// If the delta per time is small, we assume it"s a fast trackpad; otherwise we switch into wheel mode.
				this.#type = Math.abs(timeDelta * value) < 200
					? "trackpad"
					: "wheel";

				// Make sure our delayed event isn't fired again, because we accumulate
				// the previous event (which was less than 40ms ago) into this event.
				if (this.#timeout) {
					clearTimeout(this.#timeout);
					this.#timeout = null;
				}
			}

			// Slow down zoom if shift key is held for more precise zooming
			if (event.shiftKey && value) {
				value = value / 4;
			}

			// Only fire the callback if we actually know what type of scrolling device the user uses.
			if (this.#type) {
				if (!this.#active) {
					this.#start(event);
				}
			}

			// pinch zoom detected by Control key press -> do not zoom page
			if (event.ctrlKey) {
				event.preventDefault();
			}
		}, false);
	}

	isZooming() {
		return !!this.#zooming;
	}

	isActive() {
		return !!this.#active;
	}

	#onTimeout = (initialEvent: WheelEvent) => {
		this.#type = "wheel";

		if (!this.#active) {
			this.#start(initialEvent);
		}
	};

	#start(event: WheelEvent) {
		this.#active = true;

		if (!this.isZooming()) {
			this.#zooming = true;
		}

		this.#renderFrame(event);
	}

	async #renderFrame(event: WheelEvent) {
		if (!this.isActive()) {
			return;
		}

		if (this.#type === "wheel") {
			const zoom = this.#zoomAnim
				? this.#startZoom
				: await this.#wasm.getZoom();
			const direction = event.deltaY > 0
				? -1
				: 1;
			const newZoom = zoom + (direction * WHEEL_STEP);

			this.#startZoom = newZoom;

			this.#wasm.zoomToPixel({
				x: event.clientX,
				y: event.clientY,
			}, newZoom, WHEEL_TO);

			this.#zoomAnim = setTimeout(() => {
				this.#zoomAnim = null;
			}, WHEEL_TO);
		} else if (this.#type === "trackpad") {
			let divider = 1;

			if (event.deltaMode === 0) {
				if (event.ctrlKey === false) {
					// real mouse wheel, deltaY is in pixels
					divider = 1000;
				} else {
					// touchpad pinch zoom to wheel emulation
					divider = 85;
				}
			} else {
				// deltaY is in pages
				divider = 12;
			}

			this.#wasm.mapControl?.onMouseWheel(event.offsetX, event.offsetY, event.deltaY / divider);
		}

		this.#active = false;
		this.#zooming = false;
	}
}
